'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function TimeCalculator() {
  const t = useTranslations("tools.timeCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Calculator state
  const [inputs, setInputs] = useState({
    startTime: '09:00',
    endTime: '17:00',
    breakMinutes: 30,
    calculationType: 'duration'
  });

  const [results, setResults] = useState<{
    totalHours: number;
    totalMinutes: number;
    workingHours: number;
    workingMinutes: number;
    decimalHours: number;
    breakHours: number;
  } | null>(null);

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    const [startHour, startMin] = inputs.startTime.split(':').map(Number);
    const [endHour, endMin] = inputs.endTime.split(':').map(Number);

    let startMinutes = startHour * 60 + startMin;
    let endMinutes = endHour * 60 + endMin;

    // Handle overnight shifts
    if (endMinutes < startMinutes) {
      endMinutes += 24 * 60;
    }

    const totalMinutes = endMinutes - startMinutes;
    const totalHours = Math.floor(totalMinutes / 60);
    const remainingMinutes = totalMinutes % 60;

    const workingMinutes = totalMinutes - inputs.breakMinutes;
    const workingHours = Math.floor(workingMinutes / 60);
    const workingRemainingMinutes = workingMinutes % 60;

    const decimalHours = parseFloat((workingMinutes / 60).toFixed(2));
    const breakHours = parseFloat((inputs.breakMinutes / 60).toFixed(2));

    setResults({
      totalHours,
      totalMinutes: remainingMinutes,
      workingHours,
      workingMinutes: workingRemainingMinutes,
      decimalHours,
      breakHours
    });
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6, maxWidth: '800px', margin: '0 auto' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
      marginBottom: '2rem',
      borderRadius: '1rem',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)'
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 2rem', borderRadius: '1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    { maxWidth: '100%', margin: '0 auto' },
    {},
    { maxWidth: '600px' },
    { maxWidth: '800px' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem', marginBottom: '1.25rem' },
    { fontSize: '2rem', marginBottom: '1.5rem' }
  );

  const formGroupStyle = getResponsiveStyle(
    { marginBottom: '1rem' },
    {},
    { marginBottom: '1.25rem' },
    { marginBottom: '1.5rem' }
  );

  const labelStyle = getResponsiveStyle(
    { display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: colors.textPrimary },
    { fontSize: '0.875rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' }
  );

  const inputStyle = getResponsiveStyle(
    {
      width: '100%',
      border: '2px solid rgba(255, 90, 112, 0.2)',
      borderRadius: '0.5rem',
      outline: 'none',
      transition: 'all 0.3s ease',
      boxSizing: 'border-box' as const
    },
    { padding: '0.75rem 1rem', fontSize: '1rem' },
    { padding: '0.875rem 1.25rem', fontSize: '1.125rem' },
    { padding: '1rem 1.5rem', fontSize: '1.25rem' }
  );

  const selectStyle = {
    ...inputStyle,
    cursor: 'pointer',
    appearance: 'none' as const,
    backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 12 12\'%3E%3Cpath fill=\'%23ff5a70\' d=\'M6 9L1 4h10z\'/%3E%3C/svg%3E")',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 1rem center'
  };

  const calculateButtonStyle = getResponsiveStyle(
    {
      width: '100%',
      fontWeight: 700,
      color: '#fff',
      background: 'linear-gradient(135deg, #ff5a70 0%, #ffc400 100%)',
      border: 'none',
      borderRadius: '0.5rem',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      boxShadow: '0 4px 15px rgba(255, 90, 112, 0.3)',
      minHeight: '44px'
    },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '1rem 2rem', fontSize: '1.125rem', borderRadius: '0.625rem' },
    { padding: '1.25rem 2.5rem', fontSize: '1.25rem', borderRadius: '0.75rem' }
  );

  const resultsCardStyle = getResponsiveStyle(
    {
      background: 'rgba(255, 255, 255, 0.9)',
      borderRadius: '0.75rem',
      marginTop: '1rem',
      border: '1px solid rgba(255, 90, 112, 0.1)',
      boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)'
    },
    { padding: '1rem' },
    { padding: '1.5rem', borderRadius: '1rem', marginTop: '1.5rem' },
    { padding: '2rem', borderRadius: '1.25rem', marginTop: '2rem' }
  );

  const resultItemStyle = getResponsiveStyle(
    {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '0.75rem 0',
      borderBottom: '1px solid rgba(0, 0, 0, 0.05)'
    },
    {},
    { padding: '1rem 0' },
    { padding: '1.25rem 0' }
  );

  const resultLabelStyle = getResponsiveStyle(
    { color: colors.textSecondary, fontWeight: 500 },
    { fontSize: '0.875rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' }
  );

  const resultValueStyle = getResponsiveStyle(
    { color: colors.primary, fontWeight: 700 },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.5rem' }
  );

  const glassCardStyle = getResponsiveStyle(
    {
      background: 'rgba(255, 255, 255, 0.6)',
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: '1px solid rgba(255, 200, 180, 0.2)',
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '1.5rem'
    },
    { padding: '1.5rem' },
    { padding: '2rem', borderRadius: '1.25rem', marginBottom: '2rem' },
    { padding: '2.5rem', borderRadius: '1.5rem', marginBottom: '2.5rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem', lineHeight: 1.7 },
    { fontSize: '1.25rem', lineHeight: 1.8 }
  );

  const listStyle = getResponsiveStyle(
    { marginLeft: '1.5rem', color: colors.textSecondary },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const listItemStyle = {
    marginBottom: '0.75rem',
    lineHeight: 1.6
  };

  const featureGridStyle = getResponsiveStyle(
    { display: 'grid', gap: '1rem', marginTop: '1.5rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: 'repeat(2, 1fr)', gap: '1.5rem' },
    { gridTemplateColumns: 'repeat(3, 1fr)', gap: '2rem' }
  );

  const featureCardStyle = getResponsiveStyle(
    {
      background: 'rgba(255, 255, 255, 0.8)',
      borderRadius: '0.75rem',
      border: '1px solid rgba(255, 90, 112, 0.1)',
      transition: 'all 0.3s ease'
    },
    { padding: '1rem' },
    { padding: '1.25rem', borderRadius: '1rem' },
    { padding: '1.5rem', borderRadius: '1.25rem' }
  );

  const featureTitleStyle = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const featureDescStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.5 },
    { fontSize: '0.875rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' }
  );

  const faqItemStyle = getResponsiveStyle(
    {
      background: 'rgba(255, 255, 255, 0.5)',
      borderRadius: '0.75rem',
      border: '1px solid rgba(255, 90, 112, 0.1)',
      marginBottom: '1rem'
    },
    { padding: '1rem' },
    { padding: '1.25rem', borderRadius: '1rem' },
    { padding: '1.5rem', borderRadius: '1.25rem' }
  );

  const faqQuestionStyle = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const faqAnswerStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '0.875rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' }
  );

  const refLinkStyle = {
    color: colors.primary,
    textDecoration: 'none',
    transition: 'color 0.3s ease',
    wordBreak: 'break-word' as const
  };

  return (
    <div style={containerStyle}>
      {/* Header */}
      <header style={headerStyle}>
        <h1 style={h1Style}>{t("title")}</h1>
        <p style={summaryTextStyle}>{t("subtitle")}</p>
      </header>

      {/* Calculator Tool */}
      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <h2 style={h2Style}>{t("toolTitle")}</h2>
          <form onSubmit={handleCalculate}>
            <div style={formGroupStyle}>
              <label style={labelStyle}>{t("calculationType")}</label>
              <select
                style={selectStyle}
                value={inputs.calculationType}
                onChange={(e) => setInputs({...inputs, calculationType: e.target.value})}
              >
                <option value="duration">{t("durationOption")}</option>
                <option value="add">{t("addTimeOption")}</option>
                <option value="subtract">{t("subtractTimeOption")}</option>
              </select>
            </div>

            <div style={formGroupStyle}>
              <label style={labelStyle}>{t("startTime")}</label>
              <input
                type="time"
                style={inputStyle}
                value={inputs.startTime}
                onChange={(e) => setInputs({...inputs, startTime: e.target.value})}
                required
              />
            </div>

            <div style={formGroupStyle}>
              <label style={labelStyle}>{t("endTime")}</label>
              <input
                type="time"
                style={inputStyle}
                value={inputs.endTime}
                onChange={(e) => setInputs({...inputs, endTime: e.target.value})}
                required
              />
            </div>

            <div style={formGroupStyle}>
              <label style={labelStyle}>{t("breakMinutes")}</label>
              <input
                type="number"
                style={inputStyle}
                value={inputs.breakMinutes}
                onChange={(e) => setInputs({...inputs, breakMinutes: parseFloat(e.target.value) || 0})}
                min="0"
                step="1"
                placeholder="30"
              />
            </div>

            <button
              type="submit"
              style={calculateButtonStyle}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
            >
              {t("calculateButton")}
            </button>
          </form>

          {results && (
            <div style={resultsCardStyle}>
              <h3 style={{...h2Style, marginBottom: '1rem'}}>{t("resultsTitle")}</h3>

              <div style={resultItemStyle}>
                <span style={resultLabelStyle}>{t("totalTime")}</span>
                <span style={resultValueStyle}>
                  {results.totalHours}h {results.totalMinutes}m
                </span>
              </div>

              <div style={resultItemStyle}>
                <span style={resultLabelStyle}>{t("workingTime")}</span>
                <span style={resultValueStyle}>
                  {results.workingHours}h {results.workingMinutes}m
                </span>
              </div>

              <div style={resultItemStyle}>
                <span style={resultLabelStyle}>{t("decimalHours")}</span>
                <span style={resultValueStyle}>
                  {results.decimalHours} {t("hours")}
                </span>
              </div>

              <div style={{...resultItemStyle, borderBottom: 'none'}}>
                <span style={resultLabelStyle}>{t("breakTime")}</span>
                <span style={resultValueStyle}>
                  {results.breakHours} {t("hours")}
                </span>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Intro Section */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("introTitle")}</h2>
        <p style={paragraphStyle}>{t("introParagraph1")}</p>
        <p style={paragraphStyle}>{t("introParagraph2")}</p>
        <p style={paragraphStyle}>{t("introParagraph3")}</p>
      </section>

      {/* How to Use */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("howToUseTitle")}</h2>
        <ol style={listStyle}>
          <li style={listItemStyle}>{t("howToStep1")}</li>
          <li style={listItemStyle}>{t("howToStep2")}</li>
          <li style={listItemStyle}>{t("howToStep3")}</li>
          <li style={listItemStyle}>{t("howToStep4")}</li>
          <li style={listItemStyle}>{t("howToStep5")}</li>
        </ol>
      </section>

      {/* Key Features */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("featuresTitle")}</h2>
        <div style={featureGridStyle}>
          <div style={featureCardStyle}>
            <h3 style={featureTitleStyle}>⚡ {t("feature1Title")}</h3>
            <p style={featureDescStyle}>{t("feature1Desc")}</p>
          </div>
          <div style={featureCardStyle}>
            <h3 style={featureTitleStyle}>🎯 {t("feature2Title")}</h3>
            <p style={featureDescStyle}>{t("feature2Desc")}</p>
          </div>
          <div style={featureCardStyle}>
            <h3 style={featureTitleStyle}>💼 {t("feature3Title")}</h3>
            <p style={featureDescStyle}>{t("feature3Desc")}</p>
          </div>
          <div style={featureCardStyle}>
            <h3 style={featureTitleStyle}>🔄 {t("feature4Title")}</h3>
            <p style={featureDescStyle}>{t("feature4Desc")}</p>
          </div>
          <div style={featureCardStyle}>
            <h3 style={featureTitleStyle}>📊 {t("feature5Title")}</h3>
            <p style={featureDescStyle}>{t("feature5Desc")}</p>
          </div>
          <div style={featureCardStyle}>
            <h3 style={featureTitleStyle}>🌐 {t("feature6Title")}</h3>
            <p style={featureDescStyle}>{t("feature6Desc")}</p>
          </div>
        </div>
      </section>

      {/* Insights */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("insightsTitle")}</h2>
        <p style={paragraphStyle}>{t("insightsParagraph1")}</p>
        <ul style={listStyle}>
          <li style={listItemStyle}>{t("insight1")}</li>
          <li style={listItemStyle}>{t("insight2")}</li>
          <li style={listItemStyle}>{t("insight3")}</li>
          <li style={listItemStyle}>{t("insight4")}</li>
          <li style={listItemStyle}>{t("insight5")}</li>
        </ul>
      </section>

      {/* Benefits */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("benefitsTitle")}</h2>
        <h3 style={featureTitleStyle}>{t("employeeBenefitsTitle")}</h3>
        <ul style={listStyle}>
          <li style={listItemStyle}>{t("employeeBenefit1")}</li>
          <li style={listItemStyle}>{t("employeeBenefit2")}</li>
          <li style={listItemStyle}>{t("employeeBenefit3")}</li>
        </ul>
        <h3 style={{...featureTitleStyle, marginTop: '1.5rem'}}>{t("employerBenefitsTitle")}</h3>
        <ul style={listStyle}>
          <li style={listItemStyle}>{t("employerBenefit1")}</li>
          <li style={listItemStyle}>{t("employerBenefit2")}</li>
          <li style={listItemStyle}>{t("employerBenefit3")}</li>
          <li style={listItemStyle}>{t("employerBenefit4")}</li>
        </ul>
      </section>

      {/* FAQ */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("faqTitle")}</h2>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t("faq1Question")}</h3>
          <p style={faqAnswerStyle}>{t("faq1Answer")}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t("faq2Question")}</h3>
          <p style={faqAnswerStyle}>{t("faq2Answer")}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t("faq3Question")}</h3>
          <p style={faqAnswerStyle}>{t("faq3Answer")}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t("faq4Question")}</h3>
          <p style={faqAnswerStyle}>{t("faq4Answer")}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t("faq5Question")}</h3>
          <p style={faqAnswerStyle}>{t("faq5Answer")}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t("faq6Question")}</h3>
          <p style={faqAnswerStyle}>{t("faq6Answer")}</p>
        </div>
      </section>

      {/* References */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("referencesTitle")}</h2>
        <ol style={listStyle}>
          <li style={listItemStyle}>
            <a
              href="https://empmonitor.com/blog/time-clock-calculator-in-your-business/"
              target="_blank"
              rel="noopener noreferrer"
              style={refLinkStyle}
              onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryHover}
              onMouseLeave={(e) => e.currentTarget.style.color = colors.primary}
            >
              {t("ref1")}
            </a>
          </li>
          <li style={listItemStyle}>
            <a
              href="https://quickbooks.intuit.com/time-tracking/resources/time-card-calculator/"
              target="_blank"
              rel="noopener noreferrer"
              style={refLinkStyle}
              onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryHover}
              onMouseLeave={(e) => e.currentTarget.style.color = colors.primary}
            >
              {t("ref2")}
            </a>
          </li>
          <li style={listItemStyle}>
            <a
              href="https://www.hellobonsai.com/blog/time-saving-calculator"
              target="_blank"
              rel="noopener noreferrer"
              style={refLinkStyle}
              onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryHover}
              onMouseLeave={(e) => e.currentTarget.style.color = colors.primary}
            >
              {t("ref3")}
            </a>
          </li>
          <li style={listItemStyle}>
            <a
              href="https://desktrack.timentask.com/blog/time-card-calculator/"
              target="_blank"
              rel="noopener noreferrer"
              style={refLinkStyle}
              onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryHover}
              onMouseLeave={(e) => e.currentTarget.style.color = colors.primary}
            >
              {t("ref4")}
            </a>
          </li>
          <li style={listItemStyle}>
            <a
              href="https://clockify.me/work-time-calculator"
              target="_blank"
              rel="noopener noreferrer"
              style={refLinkStyle}
              onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryHover}
              onMouseLeave={(e) => e.currentTarget.style.color = colors.primary}
            >
              {t("ref5")}
            </a>
          </li>
          <li style={listItemStyle}>
            <a
              href="https://www.beforesunset.ai/post/why-should-you-use-a-time-card-calculator"
              target="_blank"
              rel="noopener noreferrer"
              style={refLinkStyle}
              onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryHover}
              onMouseLeave={(e) => e.currentTarget.style.color = colors.primary}
            >
              {t("ref6")}
            </a>
          </li>
          <li style={listItemStyle}>
            <a
              href="https://www.monitask.com/en/blog/the-benefits-of-using-a-time-card-calculator-to-manage-your-work-hours"
              target="_blank"
              rel="noopener noreferrer"
              style={refLinkStyle}
              onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryHover}
              onMouseLeave={(e) => e.currentTarget.style.color = colors.primary}
            >
              {t("ref7")}
            </a>
          </li>
          <li style={listItemStyle}>
            <a
              href="https://www.workstatus.io/blog/time-attendance/how-time-management-calculators-work/"
              target="_blank"
              rel="noopener noreferrer"
              style={refLinkStyle}
              onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryHover}
              onMouseLeave={(e) => e.currentTarget.style.color = colors.primary}
            >
              {t("ref8")}
            </a>
          </li>
          <li style={listItemStyle}>
            <a
              href="https://www.ebitools.com/en/time-tracking/what-is-a-work-hours-calculator-and-how-can-it-transform-the-times-of-our-lives"
              target="_blank"
              rel="noopener noreferrer"
              style={refLinkStyle}
              onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryHover}
              onMouseLeave={(e) => e.currentTarget.style.color = colors.primary}
            >
              {t("ref9")}
            </a>
          </li>
        </ol>
      </section>
    </div>
  );
}